import type { Router } from 'vue-router';

// Pure state carriers (no store/modal imports) so pro-web-helper can use them without a circular import.

// 'DEFAULT': the Pro modal opened from anywhere else (e.g. the app header)
export type JWTVFreeTrialPlacement = 'ft' | 'sqtv' | 'DEFAULT';

// What the placement that opened the Pro modal knows: where it came from and where to land after checkout.
export type JWTVFreeTrialContext = {
	placement: JWTVFreeTrialPlacement;
	// sqtv: the (similar) title the user lands on after checkout
	titlePath: string | null;
};

/*
 * Sign in and the Stripe round trip both rebuild the app (FirebaseUI redirect, a navigation to another domain and
 * back), so the context is kept in sessionStorage (this tab only). Saved when the Pro modal opens, cleared on success.
 */
const STORAGE_KEY = 'jwtv_free_trial';

// Added to the Stripe return url, so the page it returns to knows a free trial checkout was started
export const FREE_TRIAL_EXP_QUERY = { key: 'exp', value: 'jw-pro-ft' };

export function saveFreeTrialContext(context: JWTVFreeTrialContext) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
	} catch {
		// storage unavailable, the user lands on the default page after checkout
	}
}

export function readFreeTrialContext(): JWTVFreeTrialContext | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);

		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function clearFreeTrialContext() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// nothing to clear
	}
}

// Paid: sqtv -> the SQTV title page, every other free trial -> the JWTV provider page.
// Consumes the context, null (stay on the current page) when no free trial checkout was started.
export function jwtvFreeTrialExpSuccessHref(router: Router): string | null {
	const context = readFreeTrialContext();
	clearFreeTrialContext();

	if (!context) return null;

	if (context.placement === 'sqtv' && context.titlePath) return context.titlePath;

	return router.resolve({
		name: 'app.titles.popular.provider',
		params: { provider: 'justwatch-tv' },
		query: { monetization_types: 'FREE' },
	}).href;
}
